<template>
  <div class="digital-signature-banner" :class="`is-${signature.status}`">
    <div class="banner-icon">
      <fa-icon :icon="['fal', icon]" :spin="isRunning" />
    </div>
    <div class="banner-content">
      <div class="banner-title">{{ title }}</div>
      <div class="banner-description">{{ description }}</div>
    </div>
    <div class="banner-actions">
      <button
        v-if="isFailed && canRetry"
        type="button"
        class="btn btn-sm btn-primary btn-icon btn-icon-left"
        @click="$emit('retry')"
      ><fa-icon :icon="['fal', 'redo']" /> Tentar novamente</button>
      <button
        v-if="isSigned && canDiscard"
        type="button"
        class="btn btn-sm btn-icon btn-icon-left banner-btn-discard"
        :class="{ loading: discarding }"
        :disabled="discarding"
        @click="$emit('discard')"
      ><fa-icon :icon="['fal', 'undo']" /> Descartar assinatura</button>
      <slot name="actions" />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {
  FAILED,
  PENDING,
  SIGNED,
  isRunning,
} from '@/data/digital-signature';
import { requestedSignature } from '@/mixins/digital-signature';

export default {
  props: {
    signature: {
      type: Object,
      required: true,
    },
    discarding: {
      type: Boolean,
      default: false,
    },
    canDiscard: {
      type: Boolean,
      default: true,
    },
    canRetry: {
      type: Boolean,
      default: true,
    },
    runningDescription: {
      type: String,
      default: 'A edição fica bloqueada até a assinatura ser concluída. Você pode continuar o atendimento.',
    },
  },
  watch: {
    isRequested: {
      immediate: true,
      handler(value) {
        if (value) {
          requestedSignature.id = null;
          this.$nextTick(this.scrollIntoView);
        }
      },
    },
  },
  computed: {
    isRequested() {
      return !!requestedSignature.id
        && requestedSignature.id === this.signature.id
        && this.isRunning;
    },
    isRunning() {
      return isRunning(this.signature);
    },
    isSigned() {
      return this.signature.status === SIGNED;
    },
    isFailed() {
      return this.signature.status === FAILED;
    },
    icon() {
      if (this.isSigned) return 'shield-check';
      if (this.isFailed) return 'exclamation-triangle';
      return 'sync';
    },
    title() {
      if (this.isSigned) return 'Assinado digitalmente';
      if (this.isFailed) return 'Não foi possível assinar';
      if (this.signature.status === PENDING) return 'Enviando para assinatura';
      return 'Aguardando o Bird ID';
    },
    description() {
      if (this.isSigned) {
        return [
          this.signature.signedAt
            ? `Em ${moment(this.signature.signedAt).format('DD/MM/YYYY [às] HH:mm')}`
            : 'Documento com validade jurídica ICP-Brasil',
          this.signature.validationCode
            ? `Código de validação ${this.signature.validationCode}`
            : null,
        ].filter(v => !!v).join(' · ');
      }

      if (this.isFailed) {
        return this.signature.error || 'Confira suas credenciais do Bird ID e tente novamente';
      }

      return this.runningDescription;
    },
  },
  methods: {
    scrollIntoView() {
      if (this.$el && this.$el.scrollIntoView) {
        this.$el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.digital-signature-banner {
  align-items: center;
  background-color: #FFF8E6;
  border: $border-width solid #F2C94C;
  border-left: .2rem solid $warning-color;
  border-radius: $border-radius;
  display: flex;
  gap: $layout-spacing;
  margin-bottom: $layout-spacing;
  padding: $layout-spacing $layout-spacing-lg;
  .banner-icon {
    align-items: center;
    background-color: $light-color;
    border-radius: 50%;
    box-shadow: 0 .05rem .2rem rgba($dark-color, .12);
    color: darken($warning-color, 15%);
    display: flex;
    flex: 0 0 1.8rem;
    font-size: $font-size-lg;
    height: 1.8rem;
    justify-content: center;
  }
  .banner-content {
    flex: 1;
    min-width: 0;
  }
  .banner-title {
    color: $gray-color-900;
    font-weight: 700;
  }
  .banner-description {
    color: $gray-color-dark;
    font-size: $font-size-sm;
  }
  .banner-actions {
    display: flex;
    flex-shrink: 0;
    gap: $layout-spacing-sm;
    .btn.banner-btn-discard {
      background-color: $light-color;
      border-color: rgba($error-color, .6);
      color: $error-color;
      &:hover, &:focus {
        background-color: rgba($error-color, .08);
        border-color: $error-color;
        box-shadow: none;
        color: darken($error-color, 8%);
      }
    }
    .btn.banner-btn-whatsapp {
      background-color: $light-color;
      border-color: #25D366;
      color: #128C4A;
      svg {
        color: #25D366;
      }
      &:hover, &:focus {
        background-color: #E9F9EF;
        border-color: #1EBE5A;
        box-shadow: none;
        color: #0E7A3F;
      }
    }
  }
  &.is-signed {
    background-color: #F2FAF5;
    border-color: #BFE3CD;
    border-left-color: #2FA36B;
    .banner-icon {
      color: #2FA36B;
    }
    .banner-title {
      color: #1F7A4E;
    }
  }
  &.is-failed {
    background-color: #FDF2F2;
    border-color: #F1C4C4;
    border-left-color: $error-color;
    .banner-icon {
      color: $error-color;
    }
  }
}
</style>
